import { createSelector } from 'reselect';
import utils from 'utils';
import RESERVATIONS from 'constants/reservations';
import { isDestinationCurrencySelected } from 'selectors/vehicleSelectSelectors';
import { carClassDetailsPath } from 'paths/carClassDetails';
import { vehicleUpgradeFlagPath, vehicleUpgradeDetailsPath } from 'paths/vehicleUpgrade';
import { getDidPrepay } from 'selectors/reservationFlowSelectors';

const { PREPAY_CODE, PAYLATER_CODE } = RESERVATIONS;

const getInPathVehicleUpgradeFlag = (state) => state.getIn(vehicleUpgradeFlagPath);
const getInPathVehicleUpgradeDetails = (state) => state.getIn(vehicleUpgradeDetailsPath);
const getCarClassDetails = (state) => state.getIn(carClassDetailsPath);

/* Gets all details necessary to display in path vehicle upgrade banners */
export const vehicleUpgradeSelector = createSelector(
  [
    getInPathVehicleUpgradeFlag,
    getInPathVehicleUpgradeDetails,
    getCarClassDetails,
    isDestinationCurrencySelected,
    getDidPrepay,
  ],
  (inPathUpgradeAvailable, upgradeDetails, currentCarDetails, destinationCurrencySelected, didPrepay) => {
    const upgradeDataNotAvailable = !inPathUpgradeAvailable || !upgradeDetails;

    if (upgradeDataNotAvailable) {
      return { inPathUpgradeAvailable, inPathUpgradeDetails: null };
    }

    const vehicleRates = utils.safeToJS(upgradeDetails.getIn(['vehicle_rates']));
    const chargeType = didPrepay ? PREPAY_CODE : PAYLATER_CODE;
    const ratesObj = vehicleRates?.find((item) => item.charge_type === chargeType);
    const rateType = ratesObj?.charge_type;

    const incrementalPricing = utils.config.getIsIncrementalPricingEnabled();
    const perDay = incrementalPricing ? `/${utils.i18n('vehicle_select_incremental_unit_DAILY')}` : '';

    let totalPriceObj = {};
    let priceDifferenceObj = {};
    if (destinationCurrencySelected) {
      priceDifferenceObj = utils.safeToJS(
        upgradeDetails
          ?.get('price_differences')
          ?.find((rate) => rate.get('difference_type') === `UPGRADE_${rateType}_TO_${rateType}`)
          ?.get(incrementalPricing ? 'daily_difference_amount_payment' : 'difference_amount_payment')
      );

      totalPriceObj = ratesObj.price_summary?.estimated_total_payment;
    } else {
      priceDifferenceObj = utils.safeToJS(
        upgradeDetails
          ?.get('price_differences')
          ?.find((rate) => rate.get('difference_type') === `UPGRADE_${rateType}_TO_${rateType}`)
          ?.get(incrementalPricing ? 'daily_difference_amount_view' : 'difference_amount_view')
        // Since we only need to see price difference for upgrading to the same rate type (eg: PREPAY to PREPAY)
        // we select appropriate difference object by generating this `UPGRADE_${rateType}_TO_${rateType}` id
        // that GBO uses to distinguish price difference data
      );
      totalPriceObj = ratesObj?.price_summary?.estimated_total_view;
    }

    const upgradeImagesObj = utils.safeToJS(upgradeDetails.get('images'));
    const currentImagesObj = utils.safeToJS(currentCarDetails.get('images'));

    return {
      inPathUpgradeAvailable,
      inPathUpgradeDetails: {
        upgradeClass: upgradeDetails.get('code'),
        upgradeName: upgradeDetails.get('name'),
        upgradeMakeModelOrSimilarText: upgradeDetails.get('make_model_or_similar_text'),
        priceDifference: `${utils.getFormattedPrice(priceDifferenceObj)}${perDay}`,
        total: utils.getFormattedPrice(totalPriceObj),
        upgradeImagePath: utils.getImageFromImagesObject(upgradeImagesObj),
        currentName: currentCarDetails.get('name'),
        currentMakeModelOrSimilarText: currentCarDetails.get('make_model_or_similar_text'),
        currentImagePath: utils.getImageFromImagesObject(currentImagesObj),
      },
    };
  }
);
